<template>
  <div class="ca-order-list">
    <div class="ca-order-list__panel">
      <div class="ca-order-list__header">
        <div class="ca-order-list__heading">
          <h6 class="ca-order-list__title">我的订单</h6>
          <!-- 刷新列表：加载中切换为旋转图标并禁止重复点击 -->
          <i
            :class="loading ? 'el-icon-loading' : 'el-icon-refresh'"
            class="ca-order-list__refresh"
            title="刷新"
            @click="handleRefresh"
          />
        </div>
        <el-select
          v-model="filterStatus"
          class="ca-order-list__filter"
          popper-class="ca-select-popper"
          @change="handleFilterChange"
        >
          <el-option
            v-for="o in ORDER_STATUS_OPTIONS"
            :key="o.value"
            :label="o.label"
            :value="o.value"
          />
        </el-select>
      </div>

      <div v-loading="loading" class="ca-order-list__body">
        <!-- 空态：全部订单为空与某状态下为空，文案不同 -->
        <div v-if="!loading && !list.length" class="ca-order-list__empty">
          {{ filterStatus ? '该状态下暂无订单' : '暂无订单' }}
        </div>

        <div v-for="order in list" :key="order.orderId" class="ca-order-card">
          <div class="ca-order-card__head">
            <!-- 状态文字：原型全部状态统一红色加粗（不用状态色表，详情页才分色）；
                 文案走 statusText（认证驳回 80 按「待认证」展示） -->
            <span class="ca-order-card__status">{{ statusText(order) }}</span>
            <span class="ca-order-card__date">{{ formatDate(order.createTime) }}</span>
            <span class="ca-order-card__divider">|</span>
            <span class="ca-order-card__code">订单号: {{ order.orderCode }}</span>
            <span class="ca-order-card__amount">
              订单金额 <b>¥ {{ order.totalAmount }}</b>
            </span>
          </div>
          <div class="ca-order-card__body">
            <!-- 产品图：接口下发 productImg，未配置时用申请页同款本地 CFCA 图兜底 -->
            <img
              class="ca-order-card__img"
              :src="productImgSrc(order)"
              @error="handleProductImgError"
            />
            <div class="ca-order-card__info">
              <p class="ca-order-card__name">CA在线申请</p>
              <p>业务类型：{{ order.bizTypeName }}</p>
              <p>CA使用系统：{{ order.platformName }}</p>
              <p>
                支付方式：
                <span
                  v-if="isOnline(order)"
                  class="ca-order-card__pay-tag ca-order-card__pay-tag--online"
                >
                  网上支付
                </span>
                <span
                  v-else-if="isWire(order)"
                  class="ca-order-card__pay-tag ca-order-card__pay-tag--wire"
                >
                  电汇
                </span>
                <span v-else class="ca-order-card__pay-tag">--</span>
              </p>
              <!-- 发票状态：仅已寄出(50)/已完成(60)订单展示（此时后端才下发 invoiceStatus），状态值红色 -->
              <p v-if="canInvoice(order)">
                发票状态：<span class="ca-order-card__invoice-status">{{ invoiceStatusText(order) }}</span>
              </p>
            </div>
            <div class="ca-order-card__actions">
              <el-button
                v-if="hasStatus(order, ['10']) && isOnline(order)"
                size="small"
                class="ca-btn--primary"
                :loading="payLoadingId === order.orderId"
                @click="handleAction('PAY_NOW', order)"
              >
                立即支付
              </el-button>
              <el-button
                v-if="hasStatus(order, ['10', '15']) && isWire(order)"
                size="small"
                class="ca-btn--primary"
                @click="handleAction('WIRE_INFO', order)"
              >
                查看电汇信息
              </el-button>
              <!-- 信息认证：30 待认证为通用入口（CFCA 续费单由「CA在线续费」替代）；
                   上海CA 续费单(caFunctionType=1) 在 40 认证通过 仍需读取证书信息完成认证 -->
              <el-button
                v-if="canAuth(order)"
                size="small"
                class="ca-btn--primary"
                :loading="authLoadingId === order.orderId"
                @click="handleAction('AUTH', order)"
              >
                信息认证
              </el-button>
              <!-- CA在线续费：CFCA 续费单在「30 待认证 / 40 认证通过 / 80 已驳回」状态展示，
                   进在线续签页（30/80 身份核验、40 ukey 插件制证）；30 状态下替代原「信息认证」入口（同跳续签页） -->
              <el-button
                v-if="canCFCARenew(order)"
                size="small"
                class="ca-btn--primary"
                @click="handleAction('CA_RENEW', order)"
              >
                CA在线续费
              </el-button>
              <el-button
                v-if="hasStatus(order, ['50'])"
                size="small"
                class="ca-btn--primary"
                @click="handleAction('CA_INFO', order)"
              >
                查看CA信息
              </el-button>
              <!-- 确认收货：仅上海CA 首次申请订单（caFunctionType=0）在「50 已寄出」展示，
                   确认后订单流转 60 已完成（文档 §4.11） -->
              <el-button
                v-if="canConfirmReceive(order)"
                size="small"
                class="ca-btn--primary"
                :loading="confirmReceiveId === order.orderId"
                @click="handleAction('CONFIRM_RECEIVE', order)"
              >
                确认收货
              </el-button>
              <!-- 查看驳回原因：70 已驳回（全厂商，本地原因弹框）；
                   80 认证驳回仅 CFCA 订单展示（原因为 CFCA 核验回调写入的 opinion，文档 §11.6） -->
              <el-button
                v-if="canViewRejectReason(order)"
                size="small"
                class="ca-btn--primary"
                @click="handleAction('REJECT_REASON', order)"
              >
                查看驳回原因
              </el-button>
              <!-- 修改订单：与「查看驳回原因」同口径（70 全厂商 / 80 仅 CFCA） -->
              <el-button
                v-if="canViewRejectReason(order)"
                size="small"
                class="ca-btn--danger-plain"
                @click="handleAction('ORDER_EDIT', order)"
              >
                修改订单
              </el-button>
              <!-- 取消订单仅「10 待付款」：15 已传凭证 / 20 已支付均不可取消 -->
              <el-button
                v-if="hasStatus(order, ['10'])"
                size="small"
                class="ca-btn--danger-plain"
                @click="handleAction('CANCEL_ORDER', order)"
              >
                取消订单
              </el-button>
              <el-button
                v-if="hasStatus(order, ['90'])"
                size="small"
                class="ca-btn--primary"
                @click="handleAction('CANCEL_REASON', order)"
              >
                查看取消原因
              </el-button>
              <!-- 发票按钮：状态联动（0 申请 / 2 查看 / 3 重新开票 / 1 下载），仅已寄出、已完成显示；
                   样式同取消订单（白底红边框 danger-plain） -->
              <el-button
                v-if="canInvoice(order) && ['', '0'].includes(String(order.invoiceStatus || ''))"
                size="small"
                class="ca-btn--danger-plain"
                @click="handleAction('INVOICE_APPLY', order)"
              >
                申请发票
              </el-button>
              <el-button
                v-if="canInvoice(order) && String(order.invoiceStatus) === '2'"
                size="small"
                class="ca-btn--danger-plain"
                @click="handleAction('INVOICE_VIEW', order)"
              >
                查看发票
              </el-button>
              <el-button
                v-if="canInvoice(order) && String(order.invoiceStatus) === '3'"
                size="small"
                class="ca-btn--danger-plain"
                @click="handleAction('INVOICE_APPLY', order)"
              >
                重新开票
              </el-button>
              <el-button
                v-if="canInvoice(order) && String(order.invoiceStatus) === '1'"
                size="small"
                class="ca-btn--danger-plain"
                @click="handleAction('INVOICE_DOWNLOAD', order)"
              >
                下载发票
              </el-button>
              <el-button
                type="text"
                class="ca-order-card__link"
                @click="handleAction('ORDER_DETAIL', order)"
              >
                订单详情
              </el-button>
            </div>
          </div>
        </div>

        <!-- 分页（每页 10 条） -->
        <el-pagination
          v-if="total > 0"
          class="ca-order-list__pagination"
          background
          layout="prev, pager, next, total"
          :page-size="pagination.size"
          :current-page.sync="pagination.current"
          :total="total"
          @current-change="fetchList"
        />
      </div>
    </div>

    <!-- 弹框组：查看CA信息 / 驳回·取消原因 / 取消二次确认 / 认证说明 / 支付结果 -->
    <ca-info-dialog :visible.sync="caInfoVisible" :order="activeOrder" />
    <reason-dialog :visible.sync="reasonVisible" :type="reasonType" :order="activeOrder" />
    <cancel-order-dialog
      :visible.sync="cancelVisible"
      :order="activeOrder"
      :loading="cancelLoading"
      @confirm="handleCancelConfirm"
    />
    <auth-intro-dialog :visible.sync="authIntroVisible" />
    <!-- 上海CA续签：CA 信息获取读证书 → 确定后调 shcaAuthRenew（caSerialNo 必传） -->
    <ca-binding-dialog ref="caBindingDialog" @success="handleSheCaAuthSuccess" />
    <pay-result-dialog
      :visible.sync="payResultVisible"
      @problem="handlePayProblem"
      @done="handlePayDone"
    />
    <!-- 发票：申请（含重新开票） / 查看 -->
    <invoice-apply-dialog
      :visible.sync="invoiceApplyVisible"
      :order="activeOrder"
      @success="handleInvoiceSuccess"
    />
    <invoice-view-dialog :visible.sync="invoiceViewVisible" :order="activeOrder" />
  </div>
</template>

<script>
import CaInfoDialog from '../components/CaInfoDialog.vue'
import ReasonDialog from '../components/ReasonDialog.vue'
import CancelOrderDialog from '../components/CancelOrderDialog.vue'
import AuthIntroDialog from '../components/AuthIntroDialog.vue'
import PayResultDialog from '../components/PayResultDialog.vue'
import InvoiceApplyDialog from '../components/InvoiceApplyDialog.vue'
import InvoiceViewDialog from '../components/InvoiceViewDialog.vue'
import CaBindingDialog from '../components/CaBindingDialog.vue'
import { getOrderPage, cancelOrder, paySign, shcaAuthApply, shcaConfirmReceive } from '@/api/ca'
import {
  ORDER_STATUS_OPTIONS,
  PAY_TYPE,
  INVOICE_STATUS_TEXT,
  orderStatusText,
} from '../util/caForm'
import { payCaOrder, consumePayInitiated } from '../util/pay'

export default {
  name: 'CaOrderList',
  components: {
    CaInfoDialog,
    ReasonDialog,
    CancelOrderDialog,
    AuthIntroDialog,
    PayResultDialog,
    InvoiceApplyDialog,
    InvoiceViewDialog,
    CaBindingDialog,
  },
  data() {
    return {
      ORDER_STATUS_OPTIONS,
      filterStatus: '', // 状态筛选值（'' = 全部订单，选项值为数字码库值原样透传）；切换后页码重置为 1 重新查询
      list: [], // 当前页订单列表
      total: 0, // 符合条件的订单总数（分页用）
      loading: false, // 列表加载中
      pagination: { current: 1, size: 10 }, // 分页：每页 10 条
      activeOrder: {}, // 当前操作的订单（查看CA信息 / 原因弹框 / 取消订单 共用）
      caInfoVisible: false, // 「CA信息」弹框开关（仅已寄出订单）
      reasonVisible: false, // 通用原因弹框开关（驳回 / 取消共用）
      reasonType: 'reject', // 原因弹框类型：'reject' 驳回原因 / 'cancel' 取消原因
      cancelVisible: false, // 取消订单二次确认弹框开关
      cancelLoading: false, // 取消订单请求中
      authIntroVisible: false, // CFCA 申请单「信息认证说明」弹框开关
      payResultVisible: false, // 支付确认弹框开关（发起网上支付后弹出）
      payResultOrder: {}, // 触发支付确认弹框的那笔订单
      payLoadingId: '', // 正在调 pay/sign 的订单 id → 对应「立即支付」按钮 loading 防重复
      authLoadingId: '', // 正在调 sheca 认证下单的订单 id → 对应「信息认证」按钮 loading 防重复
      confirmReceiveId: '', // 正在调确认收货接口的订单 id → 对应「确认收货」按钮 loading 防重复
      invoiceApplyVisible: false, // 申请/重新开票弹框开关
      invoiceViewVisible: false, // 查看发票弹框开关
    }
  },
  computed: {
    token() {
      return this.$store.getters.token
    },
  },
  watch: {
    // 未登录进入订单列表时接口返回 401 并弹出登录框（守卫不阻断导航），
    // 登录成功后 token 变为有效值，自动重新查询列表，无需手动刷新浏览器
    token(newToken) {
      if (newToken) {
        this.fetchList()
      }
    },
  },
  created() {
    this.fetchList()
  },
  methods: {
    // ---- 按钮显隐判定（细化设计 §11.4 状态 × 支付方式 互斥矩阵，状态码释义见 util/caForm.js ORDER_STATUS_TEXT） ----
    // 状态码判断：库值序列化为字符串（文档 §1.3），String() 兜底防 number 形态
    hasStatus(order, codes) {
      return codes.includes(String(order.orderStatus))
    },
    isWire(order) {
      return Number(order.payType) === PAY_TYPE.WIRE
    },
    isOnline(order) {
      return Number(order.payType) === PAY_TYPE.ONLINE
    },
    // 发票入口条件：订单「已寄出(50) / 已完成(60)」才可申请/查看发票
    canInvoice(order) {
      return this.hasStatus(order, ['50', '60'])
    },
    // 查看驳回原因 / 修改订单入口：70 已驳回（全厂商）；80 认证驳回仅 CFCA 订单展示
    // （80 的驳回原因来自 CFCA 身份核验回调写入的 opinion，文档 §11.6；上海CA 的 80 仍不展示，需求 189769）
    canViewRejectReason(order) {
      return (
        this.hasStatus(order, ['70']) ||
        (order.caTypeName === 'CFCA' && this.hasStatus(order, ['80']))
      )
    },
    // 确认收货入口：仅上海CA 首次申请订单（caFunctionType=0，续费单不展示）在「50 已寄出」展示
    canConfirmReceive(order) {
      return (
        order.caTypeName === '上海CA' &&
        String(order.caFunctionType) !== '1' &&
        this.hasStatus(order, ['50'])
      )
    },
    // CA在线续费入口：CFCA 续费单（caFunctionType=1）在「30 待认证 / 40 认证通过 / 80 已驳回」状态展示
    // （文档 §4.1.1：30/80 进身份核验，40 进制证）
    canCFCARenew(order) {
      return (
        order.caTypeName === 'CFCA' &&
        String(order.caFunctionType) === '1' &&
        this.hasStatus(order, ['30', '40', '80'])
      )
    },
    // 信息认证入口：30 待认证（CFCA 续费单另有「CA在线续费」，不再重复展示）；
    // 上海CA 续费单（caFunctionType=1）在 40 认证通过 也需展示，用于读取证书信息完成续签认证
    canAuth(order) {
      if (this.hasStatus(order, ['30']) && !this.canCFCARenew(order)) return true
      return (
        order.caTypeName === '上海CA' &&
        String(order.caFunctionType) === '1' &&
        this.hasStatus(order, ['40'])
      )
    },
    invoiceStatusText(order) {
      return INVOICE_STATUS_TEXT[String(order.invoiceStatus)] || '待申请'
    },
    // 状态文案：认证驳回(80) 按「待认证」展示（口径见 util/caForm.js orderStatusText）
    statusText(order) {
      return orderStatusText(order)
    },
    // 下载发票：电票版式文件地址（eInvoiceUrl）由百望云开具成功回填，新窗口打开下载；
    // 版式生成延迟时为空，提示稍后重试
    handleInvoiceDownload(order) {
      if (!order.eInvoiceUrl) {
        this.$message.warning('发票版式文件生成中，请稍后重试')
        return
      }
      window.open(order.eInvoiceUrl, '_blank')
    },
    handleInvoiceSuccess() {
      this.invoiceApplyVisible = false
      this.fetchList()
    },
    productImgSrc(order) {
      // 与申请页一致：productFileId 是文件 ID，需拼下载接口前缀；未配置用本地 CFCA 图兜底
      return order.productFileId
        ? '/zcjf/base/file/download/' + order.productFileId
        : require('@/views/ca/assets/CFCA.jpg')
    },
    handleProductImgError(e) {
      const fallback = require('@/views/ca/assets/CFCA.jpg')
      if (e.target.src !== fallback) e.target.src = fallback
    },
    formatDate(createTime) {
      // 列表卡头行日期格式：2026年08月30日（注意与详情页摘要表的「2026.08.30」不同）
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(createTime || '')
      return m ? `${m[1]}年${m[2]}月${m[3]}日` : createTime || '--'
    },
    handleFilterChange() {
      this.pagination.current = 1
      this.fetchList()
    },
    // 刷新当前筛选/页码下的列表，加载中避免重复请求
    handleRefresh() {
      if (this.loading) return
      this.fetchList()
    },
    fetchList() {
      this.loading = true
      getOrderPage({
        status: this.filterStatus,
        current: this.pagination.current,
        size: this.pagination.size,
      })
        .then((res) => {
          const data = res.data || {}
          this.list = data.records || []
          this.total = data.total || 0
          this.checkPayResult(this.list)
        })
        .finally(() => {
          this.loading = false
        })
    },
    /**
     * 支付确认弹框的触发：只对「真正发起过网上支付」的订单提醒（申请页确认下单 /
     * 详情页「立即支付」打开收银台时会打 sessionStorage 标记，见 util/pay.js）。
     * 仅凭「网上支付 + 待付款」不能弹——刚登录就有历史待付款订单时会误弹。
     * 标记消费即删，同一订单只提醒一次；本页点「立即支付」不走标记，直接弹（见 handlePayNow）。
     */
    checkPayResult(records) {
      const hit = records.find(
        (o) =>
          Number(o.payType) === PAY_TYPE.ONLINE &&
          String(o.orderStatus) === '10' && // 待付款
          consumePayInitiated(o.orderId)
      )
      if (hit) {
        this.payResultOrder = hit
        this.payResultVisible = true
      }
    },
    handlePayProblem() {
      this.$router.push({ name: 'ca-contact' })
    },
    handlePayDone() {
      this.$router.push({
        name: 'ca-order-detail',
        query: { orderId: this.payResultOrder.orderId },
      })
    },
    /**
     * 订单卡按钮统一分发。约定：
     *  - 跳转一律用路由 name + query 传 orderId（orderId 不进路由 path，客户编码由守卫
     *    存进 store、跳转不需要带）；
     *  - 弹框类按钮只改 visible 开关与 activeOrder。
     */
    handleAction(code, order) {
      const orderId = order.orderId
      switch (code) {
        // 【订单详情】
        case 'ORDER_DETAIL':
          this.$router.push({ name: 'ca-order-detail', query: { orderId } })
          break
        // 【修改订单】
        case 'ORDER_EDIT':
          this.$router.push({ name: 'ca-order-edit', query: { orderId } })
          break
        // 【查看电汇信息】
        case 'WIRE_INFO':
          this.$router.push({ name: 'ca-wire-transfer', query: { orderId } })
          break
        // 【立即支付】
        case 'PAY_NOW':
          this.handlePayNow(order)
          break
        // 【信息认证】
        case 'AUTH':
          this.handleAuth(order)
          break
        // 【CA在线续费】
        case 'CA_RENEW':
          // CFCA 续费单进在线续签页（与 handleAuth 的 CFCA 续费分支同一目标）
          this.$router.push({ name: 'ca-order-renew', query: { orderId } })
          break
        // 【查看CA信息】
        case 'CA_INFO':
          this.activeOrder = order
          this.caInfoVisible = true
          break
        // 【确认收货】上海CA 首次申请订单（50 已寄出 → 60 已完成）
        case 'CONFIRM_RECEIVE':
          this.handleConfirmReceive(order)
          break
        // 【查看驳回原因】
        case 'REJECT_REASON':
          // 驳回原因弹框：70 已驳回（全厂商）/ 80 认证驳回（仅 CFCA，按钮显隐见 canViewRejectReason）
          this.activeOrder = order
          this.reasonType = 'reject'
          this.reasonVisible = true
          break
        // 【查看取消原因】
        case 'CANCEL_REASON':
          this.activeOrder = order
          this.reasonType = 'cancel'
          this.reasonVisible = true
          break
        // 【申请发票 / 重新开票】
        case 'INVOICE_APPLY':
          this.activeOrder = order
          this.invoiceApplyVisible = true
          break
        // 【查看发票】
        case 'INVOICE_VIEW':
          this.activeOrder = order
          this.invoiceViewVisible = true
          break
        // 【下载发票】
        case 'INVOICE_DOWNLOAD':
          this.handleInvoiceDownload(order)
          break
        // 【取消订单】
        case 'CANCEL_ORDER':
          this.activeOrder = order
          this.cancelVisible = true
          break
        default:
          break
      }
    },
    handlePayNow(order) {
      this.payLoadingId = order.orderId
      paySign({ orderId: order.orderId })
        .then((res) => {
          // 收银台在新窗口打开后，本页立即弹支付结果确认（弹框文案即提示去新窗口支付）
          if (payCaOrder(res.data)) {
            this.payResultOrder = order
            this.payResultVisible = true
          }
        })
        .finally(() => {
          this.payLoadingId = ''
        })
    },
    /**
     * 「信息认证」按钮的分流（订单需处于「待认证」状态才会出现该按钮）：
     *  - CFCA 续费 → 进在线续签页（ukey 插件续签）；
     *  - CFCA 申请 → 弹「信息认证说明」弹框（认证由认证中心短信引导，前端不承担跳转）；
     *  - 上海CA 续期 → 弹 CA 信息认证弹框（CA 登录读证书序列号后调 shcaAuthRenew）；
     *  - 上海CA 申请 → 直接调申请下单接口拿 authUrl，新窗口打开认证 H5。
     */
    handleAuth(order) {
      // caTypeName 仅 CFCA / 上海CA 两种，优先按 CFCA 判断
      if (order.caTypeName === 'CFCA') {
        if (String(order.caFunctionType) === '1') {
          this.$router.push({
            name: 'ca-order-renew',
            query: { orderId: order.orderId },
          })
        } else {
          this.authIntroVisible = true
        }
        return
      }
      this.openSHECAAuth(order)
    },
    /**
     * 上海CA「信息认证」分流：
     *  - 续期（caFunctionType=1）→ 打开 CA 信息认证弹框（需先从 UKey 读取证书序列号 caSerialNo，
     *    该参数是 shcaAuthRenew 的必传项），确定后再换取认证 URL；
     *  - 申请 → 直接调申请下单接口换取认证 URL，新窗口打开。
     */
    openSHECAAuth(order) {
      if (String(order.caFunctionType) === '1') {
        this.openSHECARenewBind(order)
        return
      }
      this.openSHECAApplyAuth(order)
    },
    // 上海CA续签：打开 CA 信息认证弹框（获取证书信息），确定后由弹框调 shcaAuthRenew
    openSHECARenewBind(order) {
      this.$refs.caBindingDialog.open(order.orderId)
    },
    // 上海CA申请：直接调证书申请下单接口拿 authUrl，新窗口打开认证 H5
    // frontRedirectUrl 传当前页全路径，认证完成后由认证 H5 跳转带回
    openSHECAApplyAuth(order) {
      this.authLoadingId = order.orderId
      shcaAuthApply({ orderId: order.orderId, frontRedirectUrl: window.location.href })
        .then((res) => this.handleSheCaAuthSuccess(res.data && res.data.authUrl))
        .catch(() => {
          // 业务错误提示由 http 拦截器统一 toast，这里仅兜住 rejection
        })
        .finally(() => {
          this.authLoadingId = ''
        })
    },
    // 打开上海CA认证 H5（申请直接返回 / 续签弹框确认成功后返回共用）
    handleSheCaAuthSuccess(url) {
      // authUrl 缺失不是接口报错，拦截器不会提示，这里单独兜底
      if (!url) {
        this.$message.error('操作失败，请重试')
        return
      }
      window.open(url)
    },
    /**
     * 确认收货（文档 §4.11）：仅上海CA 首次申请订单、50 已寄出可调。
     * 二次确认后调接口，成功后订单流转 60 已完成，刷新列表。
     */
    handleConfirmReceive(order) {
      this.$confirm(
        '请确认您已收到 UKey 实物。<br/>确认收货后，系统会将证书 PIN 码发送至您预留的<b>手机号码</b>和<b>邮箱</b>，请妥善保管。',
        '确认收货',
        {
          type: 'warning',
          confirmButtonText: '确认收货',
          cancelButtonText: '取消',
          // 文案为前端静态内容（含换行），非用户输入，可安全按 HTML 渲染
          dangerouslyUseHTMLString: true,
          // 按钮套用 CA 主题类（同开票申请弹框：确认红底、取消红字红边）
          confirmButtonClass: 'ca-btn--primary',
          cancelButtonClass: 'ca-btn--danger-plain',
          // MessageBox 挂到 body，需 customClass 定位后覆盖右上角关闭图标的悬浮色
          customClass: 'ca-confirm-box',
        }
      )
        .then(() => {
          this.confirmReceiveId = order.orderId
          return shcaConfirmReceive({ orderId: order.orderId })
            .then(() => {
              this.$message.success('确认收货成功')
              this.fetchList()
            })
            .finally(() => {
              this.confirmReceiveId = ''
            })
        })
        .catch(() => {
          // 用户取消二次确认（或接口报错已由拦截器 toast，此处兜住 rejection）
        })
    },
    handleCancelConfirm() {
      this.cancelLoading = true
      cancelOrder({
        orderId: this.activeOrder.orderId,
        cancelReason: '用户主动取消订单', // 前端固定值，页面无原因输入框（细化设计 §11.6 / D9）
      })
        .then(() => {
          this.cancelVisible = false
          this.$message.success('订单取消成功')
          this.fetchList()
        })
        .finally(() => {
          this.cancelLoading = false
        })
    },
  },
}
</script>
